import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputLabel,
  FormControl,
  Tooltip,
} from "@mui/material";
import { Textarea } from "@mui/joy";
import CloseIcon from "../../../../../assets/icons/closeIcon.svg";
import "./company-dashboard-edit-object-description-popup.scss";

/**
 * CompanyDashboardEditObjectiveDescriptionPopup
 *
 * Dialog for editing an Objective's description with a 1000-character soft limit.
 * Displays a live character counter and disables Save when invalid.
 *
 * Props (any-typed for compatibility):
 * - isOpenAddPopup: boolean — dialog open state
 * - onCancel: () => void — closes the dialog
 * - descriptionData: { objectiveDescription?: string } — seed data
 * - onSave: (payload: { description: string; empty: boolean }) => void
 */
const CompanyDashboardEditObjectiveDescriptionPopup: any = (props: any) => {
  const { isOpenAddPopup, onCancel, descriptionData, onSave } = props;
  const validationSchema = Yup.object().shape({
    description: Yup.string().max(
      1000,
      "Description cannot exceed 1000 characters",
    ),
  });
  return (
    <Dialog
      open={isOpenAddPopup}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className={`company-dashboard-add-description-popup-container `}
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onCancel();
      }}
    >
      <DialogTitle id="alert-dialog-title">
        <div className="header-wrapper">
          <div className="header-wrapper-text">Edit Description</div>
          <div className="image-wrapper">
            <button
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
              onClick={onCancel}
            >
              <img
                src={CloseIcon}
                className="close-icon"
                style={{ cursor: "pointer" }}
                alt="close"
              />
            </button>
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <Formik
            initialValues={{
              description: descriptionData?.objectiveDescription || "",
            }}
            validationSchema={validationSchema}
            validateOnChange={true}
            onSubmit={(values) => {
              const descObj = { description: values.description, empty: false };
              onSave(descObj);
              onCancel();
            }}
          >
            {({
              values,
              setFieldValue,
              errors,
              isValid,
              submitCount,
              handleSubmit,
              touched,
            }) => {
              const charCount = values.description?.length || 0;
              const remaining = 1000 - charCount;
              return (
                <>
                  <Form>
                    <div className="objective-popup-confirmation-description-checkbox-input">
                      <div className="form-group form-text-input-component">
                        <InputLabel>Description</InputLabel>
                        <FormControl>
                          <Textarea
                            color="neutral"
                            disabled={false}
                            minRows={3}
                            sx={{
                              resize: "vertical",
                              paddingRight: "16px", // space for scrollbar
                              "& textarea": {
                                paddingRight: "16px !important", // ensure scrollbar doesn’t overlap text
                                boxSizing: "border-box",
                              },
                            }}
                            variant="outlined"
                            value={values.description}
                            onChange={(e) => {
                              setFieldValue("description", e?.target?.value);
                            }}
                            placeholder="Enter description here"
                          />
                        </FormControl>
                        <div
                          className={`char-counter ${
                            remaining < 0 ? "error-message" : ""
                          }`}
                        >
                          {charCount} / 1000 characters
                        </div>
                        {touched.description && errors.description && (
                          <div className="error-message">
                            {errors.description as string}
                          </div>
                        )}
                      </div>
                    </div>
                  </Form>
                  <DialogActions>
                    <div className="action-btn-wrapper">
                      <button
                        type="button"
                        onClick={onCancel}
                        className="cancel-btn"
                      >
                        Cancel
                      </button>
                      {charCount > 1000 || (submitCount > 0 && !isValid) ? (
                        <Tooltip
                          title={"Please resolve all errors before submitting."}
                          placement="top"
                        >
                          <span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                handleSubmit();
                              }}
                              autoFocus
                              className="action-btn"
                              disabled
                              style={{
                                opacity: 0.5,
                                cursor: "not-allowed",
                              }}
                            >
                              Save
                            </button>
                          </span>
                        </Tooltip>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            handleSubmit();
                          }}
                          autoFocus
                          className="action-btn"
                          style={{
                            opacity: 1,
                            cursor: "pointer",
                          }}
                        >
                          Save
                        </button>
                      )}
                    </div>
                  </DialogActions>
                </>
              );
            }}
          </Formik>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyDashboardEditObjectiveDescriptionPopup;
